/* eslint-disable max-lines -- I can't find any logical way to split this file */
import {
  ChangeDetectionStrategy,
  Component,
  Injector,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { spreadObjectStore$ } from '@sama/app-state';
import { assert, isDefined } from '@sama/js-core';
import { isEmpty } from '@sama/micro-dash';
import { withHistory } from '@sama/rxjs-core';
import { concat, difference, isEqual, isUndefined, values } from 'lodash';
import { BehaviorSubject, combineLatest, lastValueFrom, merge, of } from 'rxjs';
import {
  debounceTime,
  delayWhen,
  distinctUntilChanged,
  filter,
  map,
  mergeAll,
  switchMap,
  take,
  takeWhile,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { isShapeGroupOrderState2D } from '../../../core/h3ml/state/shape-typing.utils';
import { shouldRenderOrthographic } from '../../../core/outputs/annotation/annotation-shared/fusion/projection/orthographic-orchestrator';
import { flipShapeYCoordinates } from '../../../core/outputs/annotation/annotation-shared/fusion/projection/orthographic-utils';
import {
  answerHelper2D,
  answerHelper3D,
} from '../../../core/outputs/annotation/annotation-shared/shape-layer-answer-helper';
import { TransientShapeLayerState } from '../../../core/outputs/annotation/annotation-shared/state/layers/transient-shape-layer-state';
import { VectorLayerState } from '../../../core/outputs/annotation/annotation-shared/state/layers/vector-layer-state';
import { contentConnectorToken } from '../../../core/outputs/annotation/utils/misc-utils';
import { getUpdatedShapeGroupOrderAfterDeleteShapes } from '../../../cross-team-shared/annotation/shared/state/layers/shape-layer-state';
import {
  ShapeState,
  ShapeState2D,
} from '../../../cross-team-shared/annotation/shared/state/shape-state';
import { KeyboardShortcutName } from '../../../cross-team-shared/misc-services/keyboard-shortcut.service';
import {
  getUrlParam,
  objectNumberedKeys,
} from '../../../cross-team-shared/misc-utils';
import { isOrthographicVideoOutput } from '../../../cross-team-shared/misc-utils/output-utils';
import {
  getOrThrow,
  strictValues,
} from '../../../cross-team-shared/misc-utils/strict-record';
import { getFrameNumberForEventTracking } from '../../../cross-team-shared/state/sensor-renderer-state';
import { isOrthographicShape3D } from '../../../cross-team-shared/state/shape.state';
import { isOrthographicShapeState2D } from '../../../shared/misc-utilities/misc-utils';
import {
  LoadTaskResult,
  PointCloudContentConnector,
  ShapeGroupOrderState,
  VideoContentConnector,
} from '../../../team-apis/content/content-connector';
import {
  Point2D,
  VectorTaggingShapeType,
} from '../../../team-apis/otterdam/answers/shape-typings';
import { VideoAnswer } from '../../../team-apis/otterdam/answers/video-answer';
import { VideoOutput } from '../../../team-apis/otterdam/outputs/video-output';
import { Task } from '../../../team-apis/otterdam/task';
import {
  AbstractAnnotationOutputComponent,
  contentInjectorToken,
} from '../annotation-shared/abstract-annotation-output-component';
import { annotationProviders2D } from '../annotation-shared/annotation-providers';
import {
  resizeShortcuts,
  rotationShortcuts,
  translationShortcuts,
} from '../annotation-shared/output-shortcuts';
import { AbstractAnnotationStore } from '../annotation-shared/state/root/abstract-annotation-store';
import { AbstractAnnotationStore2D } from '../annotation-shared/state/root/abstract-annotation-store-2d';
import { VideoState } from './state/video-state';
import { VideoStore } from './state/video-store';
import { videoOutputshortcuts } from './video-output-shortcuts';

const shortcutsToRedirect: KeyboardShortcutName[] = [
  ...translationShortcuts,
  ...rotationShortcuts,
  ...resizeShortcuts,
];

@Component({
  selector: 'h3-video-output',
  templateUrl: './video-output.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    annotationProviders2D,
    VideoStore,
    { provide: AbstractAnnotationStore, useExisting: VideoStore },
    { provide: AbstractAnnotationStore2D, useExisting: VideoStore },
    {
      provide: contentConnectorToken,
      deps: [VideoOutputComponent],
      useFactory: (self: VideoOutputComponent) => self.contentConnector,
    },
    {
      provide: contentInjectorToken,
      deps: [VideoOutputComponent],
      useFactory: (self: VideoOutputComponent) => self.contentInjector,
    },
  ],
  standalone: false,
})
export class VideoOutputComponent
  extends AbstractAnnotationOutputComponent<
    VideoState,
    VideoOutput,
    VideoAnswer
  >
  implements OnInit, OnDestroy
{
  showAddRandomShapes$ = combineLatest([
    this.store('status').$,
    this.taskWorkspaceStore('splitViewOutputName').$,
  ]).pipe(
    map(
      ([status, splitViewOutputName]) =>
        (environment.shouldShowAddRandomShapes ||
          getUrlParam('showAddRandomShapes') === 'true') &&
        status === 'ready' &&
        splitViewOutputName === undefined,
    ),
  );
  override contentConnector!: VideoContentConnector;

  // Some features like 2D->3D projection and orthographic video require a
  // visible point-cloud primary output present in the layout. This promise
  // resolves to true when the primary output initializes so that the rest of
  // the code depending on it being present can proceed synchronously.
  primaryMinionRegistered!: Promise<true>;

  // These are tested in the keyboard shortcut service to avoid duplicating most
  // of the tests in the spec for this component.
  protected keyboardShortcuts = videoOutputshortcuts;

  private pointCloudContentConnector!: PointCloudContentConnector;
  private orthographicShapesReady$ = new BehaviorSubject(false);

  constructor(
    injector: Injector,
    public store: VideoStore,
  ) {
    super(store, injector);

    this.eventTrackingService.addContext({
      frame_number: () => getFrameNumberForEventTracking(store.state()),
    });
    this.store('frameNumber').set(
      this.fusionManager.fusionStore.state().frameNumber,
    );
  }

  override ngOnInit() {
    super.ngOnInit();
    this.primaryMinionRegistered = this.fusionManager
      .sleepUntilPrimaryIsAvailable()
      .then(() => {
        if (
          shouldRenderOrthographic(
            this.fusionManager.getPrimaryMinion(),
            this.outputInfoStore.state().output,
          )
        ) {
          this.pointCloudContentConnector = this.fusionManager
            .getPrimaryMinion()
            .getPointCloudContentConnector();
          super.setUpSubscriptions();
          this.setUpOrthographicVideo();
          this.syncOrthographicFrames();
          this.setUpOrthographicShapeSyncObjectless();
        } else {
          if (
            this.outputInfoStore.state().output.meta.is_orthographic_projection
          ) {
            this.setUpTaskLoading();
          }
        }
        return true;
      });
  }

  override ngOnDestroy() {
    super.ngOnDestroy();
    this.store.delete();
  }

  protected override setUpSubscriptions() {
    if (!this.outputInfoStore.state().output.meta.is_orthographic_projection) {
      super.setUpSubscriptions();
    }

    if (!this.taskWorkspaceStore.isUsingObjects()) {
      this.objectlessToolbarKeyboardShortcutService.register2D();
    }
  }

  protected override setUpKeyboardShortcutSubscriptions() {
    for (const shortcut of this.keyboardShortcuts) {
      this.subscribeToShortcut(shortcut);
    }
  }

  protected override async loadTask(task: Task) {
    await this.primaryMinionRegistered;

    if (
      shouldRenderOrthographic(
        this.fusionManager.getPrimaryMinion(),
        this.outputInfoStore.state().output,
      )
    ) {
      return this.loadOrthographicTask(task);
    } else {
      return this.contentConnector.loadTask(task);
    }
  }

  protected answer() {
    const shapeGroupOrder =
      this.taskWorkspaceStore.state().outputAnnotationInfo[
        this.annotationOutputName
      ];

    assert(
      // @ts-ignore -- fix me
      isShapeGroupOrderState2D(shapeGroupOrder),
      'annotation should only have 2d shapes',
    );

    const filteredShapeGroupOrder = this.shouldNotSaveFusedShapes()
      ? this.filterOutFusedShapes(shapeGroupOrder)
      : shapeGroupOrder;

    const nonBlankAnswer = answerHelper2D.shapeGroupOrderExternal(
      // @ts-ignore -- fix me
      filteredShapeGroupOrder,
      this.sensorRendererStore.state().loadedFrames.length,
    );
    let answer = isEmpty(filteredShapeGroupOrder.shapes) ? {} : nonBlankAnswer;

    const primaryMinion = this.fusionManager.getPrimaryMinion();
    if (
      shouldRenderOrthographic(
        primaryMinion,
        this.outputInfoStore.state().output,
      )
    ) {
      const orthographicOrchestrator = this.getOrthographicOrchestrator();
      orthographicOrchestrator.setVideoOutputName(this.annotationOutputName);

      // Video answer should be empty when it is orthographic
      answer = {};
    }

    return answer;
  }

  protected resetStoreForNewTask(result: LoadTaskResult) {
    const newState = new VideoState();
    if (
      shouldRenderOrthographic(
        this.fusionManager.getPrimaryMinion(),
        this.outputInfoStore.state().output,
      )
    ) {
      this.initializeOrthographicShapesObjectless();
    }

    const layer = this.createLayerState(result.width, result.height);
    newState.layers = [layer];
    newState.status = 'ready';
    this.setSensorRendererState(result);
    this.store.set(newState);
    this.resetRightPanelState();
  }

  protected createLayerState(width: number, height: number) {
    const layer = new VectorLayerState(
      width,
      height,
      new TransientShapeLayerState(),
    );

    return layer;
  }

  protected override updateShapeLayer() {
    if (
      shouldRenderOrthographic(
        this.fusionManager.getPrimaryMinion(),
        this.outputInfoStore.state().output,
      )
    ) {
      this.initializeOrthographicShapesObjectless();
    }
  }

  protected override getShapeGroupOrder() {
    const shapeGroupOrder = super.getShapeGroupOrder();

    return (
      this.getShapeGroupOrderForOrthographic(shapeGroupOrder) ||
      this.getShapeGroupOrderWithProjectedCuboid(shapeGroupOrder) ||
      shapeGroupOrder
    );
  }

  protected override getShapeGroupOrderChange$() {
    const shapeGroupOrder$ = super.getShapeGroupOrderChange$();

    const pointCloudOutputName =
      this.fusionManager.primaryMinion$.value?.output.name;
    if (
      !pointCloudOutputName ||
      isOrthographicVideoOutput(this.outputInfoStore.state().output)
    ) {
      return shapeGroupOrder$;
    }

    return merge(
      shapeGroupOrder$,
      this.taskWorkspaceStore('splitViewOutputName').$.pipe(
        switchMap((splitViewOutputName) =>
          splitViewOutputName
            ? this.getFocusedCuboidChange$(pointCloudOutputName)
            : of(undefined),
        ),
      ),
    );
  }

  // eslint-disable-next-line max-params-unless-injectable -- override
  protected override inspectFocusedShape(
    previousSelection: number[] | undefined,
    inspectMode: boolean,
    selectionChanged: boolean,
    frameNumberChanged: boolean,
    inspectModeChanged: boolean,
  ) {
    const { splitViewOutputName } = this.taskWorkspaceStore.state();

    if (
      isDefined(splitViewOutputName) &&
      this.annotationOutputName !== splitViewOutputName
    ) {
      return;
    }

    super.inspectFocusedShape(
      previousSelection,
      inspectMode,
      selectionChanged,
      frameNumberChanged,
      inspectModeChanged,
    );
  }

  private getShapeGroupOrderForOrthographic(
    shapeGroupOrder: ShapeGroupOrderState,
  ) {
    if (
      !this.taskWorkspaceStore.isUsingObjects() ||
      !isOrthographicVideoOutput(this.outputInfoStore.state().output)
    ) {
      return undefined;
    }

    const shapes: StrictRecord<number, ShapeState> = {};
    strictValues(shapeGroupOrder.shapes).forEach(
      (shape) => (shapes[shape.index] = flipShapeYCoordinates(shape)),
    );
    shapeGroupOrder.shapes = shapes;
    return shapeGroupOrder;
  }

  // eslint-disable-next-line complexity,max-lines-per-function -- easy to read
  private getShapeGroupOrderWithProjectedCuboid(
    shapeGroupOrder: ShapeGroupOrderState,
  ) {
    const { splitViewOutputName } = this.taskWorkspaceStore.state();
    const pointCloudOutputName =
      this.fusionManager.primaryMinion$.value?.output.name;
    if (
      isUndefined(splitViewOutputName) ||
      isUndefined(pointCloudOutputName) ||
      isOrthographicVideoOutput(this.outputInfoStore.state().output)
    ) {
      return undefined;
    }

    const focusedCuboid = this.getFocusedCuboid(pointCloudOutputName);
    if (!focusedCuboid) {
      return undefined;
    }

    const projectedPoints = this.fusionManager
      .getPrimaryMinion()
      .getPointCloudContentConnector()
      .getProjectedRectanglePoints(
        focusedCuboid.index,
        this.annotationOutputName,
      );

    if (!projectedPoints) {
      return undefined;
    }

    return this.appendProjectedCuboid(
      focusedCuboid,
      this.store.state().frameNumber,
      projectedPoints,
      shapeGroupOrder,
    );
  }

  private appendProjectedCuboid(
    cuboid: ShapeState,
    currentFrame: number,
    projectedPoints: Point2D[],
    shapeGroupOrder: ShapeGroupOrderState,
  ): ShapeGroupOrderState {
    const projectedRectangle: ShapeState2D = {
      ...cuboid,
      type: 'rectangle',
      key_locations: [
        { frame_number: currentFrame, points: projectedPoints, visibility: 1 },
      ],
      isProjection: true,
    };
    return {
      shapes: {
        ...shapeGroupOrder.shapes,
        [projectedRectangle.index]: projectedRectangle,
      },
      groups: shapeGroupOrder.groups,
      shapeAndGroupOrder: concat(shapeGroupOrder.shapeAndGroupOrder, {
        type: 'shape',
        index: projectedRectangle.index,
      }),
    };
  }

  private getFocusedCuboidChange$(pointCloudOutputName: string) {
    const shapeSelection$ = this.taskWorkspaceStore('shapeSelection').$;
    const selectedObjectIds$ = this.taskWorkspaceStore('objectSelection').$;

    return combineLatest([shapeSelection$, selectedObjectIds$]).pipe(
      map(() => this.getFocusedCuboid(pointCloudOutputName)),
      distinctUntilChanged(),
    );
  }

  private getFocusedCuboid(pointCloudOutputName: string) {
    const pointCloudSelection =
      this.rootShapeSelectionService.getNestedSelectionForOutput(
        pointCloudOutputName,
      );
    const videoSelection =
      this.rootShapeSelectionService.getNestedSelectionForOutput(
        this.annotationOutputName,
      );
    if (pointCloudSelection.length !== 1 || videoSelection.length) {
      return undefined;
    }

    const focusedShapeIndex = pointCloudSelection[0];
    return getOrThrow(
      this.taskWorkspaceStore.state().outputAnnotationInfo,
      pointCloudOutputName,
    ).shapes[focusedShapeIndex];
  }

  private subscribeToShortcut(shortcut: KeyboardShortcutName) {
    this.subscribeTo(
      this.keyboardShortcutService
        .getEvent$(shortcut)
        .pipe(withLatestFrom(this.store('redirectShortcutsToPointCloud').$)),
      ([
        { keyboardEvent, eventTrackingValue },
        redirectShortcutsToPointCloud,
      ]) => {
        if (
          redirectShortcutsToPointCloud &&
          shortcutsToRedirect.includes(shortcut)
        ) {
          this.fusionManager
            .getPrimaryMinion()
            .contentConnector.handleKeyboardShortcut(shortcut, keyboardEvent, {
              ...eventTrackingValue,
              outputType: this.outputInfoStore.state().output.type,
              outputId: this.outputInfoStore.state().output.id,
            });
        } else {
          this.contentConnector.handleKeyboardShortcut(
            shortcut,
            keyboardEvent,
            eventTrackingValue,
          );
        }
      },
    );
  }

  private syncOrthographicFrames() {
    this.subscribeTo(
      this.getOrthographicOrchestrator().loadedFrames$.pipe(
        delayWhen(() => this.sensorRendererStore.$.pipe(filter(isDefined))),
      ),
      (loadedFrames) => {
        this.sensorRendererStore('loadedFrames').set(loadedFrames);
      },
    );
  }

  private setUpOrthographicShapeSyncObjectless() {
    if (!this.taskWorkspaceStore.isUsingObjects()) {
      this.syncOrthographicShapes();
      this.syncOrthographicShapeDeletion();
    }
  }

  private syncOrthographicShapes() {
    const orchestrator = this.getOrthographicOrchestrator();

    this.subscribeTo(
      spreadObjectStore$(
        this.taskWorkspaceStore('outputAnnotationInfo')(
          this.annotationOutputName,
          // @ts-ignore -- fix me
        )('shapes'),
      ).pipe(
        withHistory(1),
        map(([current, past]) => difference(current, past ?? [])),
        switchMap((shapeStores) =>
          shapeStores.map((shapeStore) =>
            shapeStore.$.pipe(
              filter(() => this.orthographicShapesReady$.value),
              takeWhile(isDefined),
              debounceTime(100),
              filter(isOrthographicShapeState2D),
            ),
          ),
        ),
        mergeAll(),
        filter((shape) => shape.key_locations.length !== 0),
        map((shape) => orchestrator.convertShapeToPointCloudCoordinates(shape)),
      ),
      (shape) => {
        this.pointCloudContentConnector.setOrthographicShape(shape);
        orchestrator.answersChanged();
      },
    );
  }

  private syncOrthographicShapeDeletion() {
    this.subscribeTo(
      this.taskWorkspaceStore('outputAnnotationInfo')(
        this.annotationOutputName,
        // @ts-ignore -- fix me
      )('shapes').$.pipe(
        map(objectNumberedKeys),
        withHistory(1),
        map(([current, past]) => difference(past ?? [], current)),
        distinctUntilChanged(isEqual),
        tap((ids) => {
          this.pointCloudContentConnector.deleteOrthographicShape(ids);
        }),
      ),
    );
  }

  private async loadOrthographicTask(task: Task) {
    const pointCloudBoundingBox = this.getOrthographicBoundingBox(task);

    // Wait for the primary point cloud to have its sensorRenderer loaded
    const loadedFrames = await lastValueFrom(
      this.taskWorkspaceStore
        .getLoadedFramesStore(this.fusionManager.getPrimaryMinion().output.name)
        .$.pipe(filter(isDefined), take(1)),
    );

    const loadTaskResult: LoadTaskResult = {
      width: pointCloudBoundingBox.max[0] - pointCloudBoundingBox.min[0],
      height: pointCloudBoundingBox.max[1] - pointCloudBoundingBox.min[1],
      // @ts-ignore -- fix me
      frameCount: loadedFrames.length,
      allFramesLoaded: loadedFrames[0],
    };

    // Send task result to 2D content team, so it can build its sensor
    // renderer state.
    this.contentConnector.initForOrthographicTask(
      loadTaskResult,
      pointCloudBoundingBox,
    );

    return loadTaskResult;
  }

  private getOrthographicBoundingBox(task: Task) {
    const primaryMinion = this.fusionManager.getPrimaryMinion();

    const pointCloudBoundingBox =
      this.getOrthographicOrchestrator().computeBoundingBoxFromTask(
        task,
        primaryMinion.output.name,
      );

    return pointCloudBoundingBox;
  }

  private setUpOrthographicVideo() {
    const primaryMinion = this.fusionManager.getPrimaryMinion();

    // Copy answer options from the primary output so that they have the same
    // nested outputs
    const primaryAnswerOptions = primaryMinion.output.answer_options;
    this.outputInfoStore('output')('answer_options').set(primaryAnswerOptions);

    // Override the event tracking context to make events appear as if they
    // are coming from the primary output
    this.eventTrackingService.addContext({
      output_type: primaryMinion.output.type,
      output_id: primaryMinion.output.id,
    });
  }

  private shouldNotSaveFusedShapes() {
    return (
      this.fusionManager.getMinion(this.outputInfoStore.state().output.id) &&
      !this.hasRectangleTool()
    );
  }

  private hasRectangleTool() {
    return this.outputInfoStore
      .state()
      .output.meta.available_tools.includes('Rectangle');
  }

  private filterOutFusedShapes(
    shapeGroupOrder: ShapeGroupOrderState<
      VectorTaggingShapeType,
      Point2D,
      ShapeState<Point2D, VectorTaggingShapeType>
    >,
  ) {
    const shapesToRemove = objectNumberedKeys(shapeGroupOrder.shapes).filter(
      (index) =>
        this.fusionManager.isShapeFused(index) &&
        // @ts-ignore -- fix me
        !isOrthographicShape3D(shapeGroupOrder.shapes[index]),
    );

    return getUpdatedShapeGroupOrderAfterDeleteShapes(
      shapeGroupOrder,
      shapesToRemove,
    );
  }

  private initializeOrthographicShapesObjectless() {
    if (this.taskWorkspaceStore.isUsingObjects()) {
      return;
    }

    const orthographicOrchestrator = this.getOrthographicOrchestrator();

    const shapeGroupOrder = answerHelper2D.answerToShapeGroupOrder(
      orthographicOrchestrator.getVideoAnswerPortion(),
    );

    this.taskWorkspaceStore('outputAnnotationInfo')(
      this.annotationOutputName,
    ).assign(shapeGroupOrder);

    this.orthographicShapesReady$.next(true);

    const initialOrthographicAnswers =
      orthographicOrchestrator.getPointCloudAnswerPortion();

    const shapes = answerHelper3D.answerToShapes(initialOrthographicAnswers);
    values(shapes).forEach((shape) => {
      // @ts-ignore -- fix me
      if (isOrthographicShape3D(shape)) {
        this.pointCloudContentConnector.setOrthographicShape(shape);
      }
    });
  }

  private getOrthographicOrchestrator() {
    return this.fusionManager.getPrimaryMinion().getOrthographicOrchestrator();
  }
}
